import {
    getEnabledSettingStatuses,
    getNormalizedFilter,
    PortalStore,
    portalStoreToken,
} from '@servicetitan/cp-ui';
import { inject, injectable } from '@servicetitan/react-ioc';
import { makeObservable, observable, runInAction } from 'mobx';
import { SpecialSettingsStore } from '../../common/stores/special-settings.store';
import { EstimateStatus } from '../enums/estimate-status';
import { EstimatesFilterSettings } from '../utils/estimates-filters';

export interface EstimateSettingsFilters {
    open: boolean;
    sold: boolean;
    dismissed: boolean;
    expired: boolean;
}

export type EstimateSettingsFiltersRecord = Pick<
    EstimateSettingsFilters,
    keyof EstimateSettingsFilters
>;

@injectable()
export class EstimatesFiltersStore {
    @observable settingsFilter?: EstimateSettingsFilters;

    private loading = false;

    constructor(
        @inject(portalStoreToken) private portalStore: PortalStore,
        @inject(SpecialSettingsStore) private specialSettings: SpecialSettingsStore
    ) {
        makeObservable(this);
    }

    async init() {
        if (this.loading) {
            return;
        }

        this.loading = true;

        const settingsFilter = await this.loadEstimatesSettingsFilters();

        this.loading = false;
        runInAction(() => {
            this.settingsFilter = settingsFilter;
        });
    }

    loadEstimatesSettingsFilters = async (): Promise<EstimateSettingsFilters> => {
        const defaultEstimateFilter: EstimateSettingsFilters = {
            open: false,
            sold: false,
            dismissed: false,
            expired: false,
        };
        try {
            const result = await this.specialSettings.getAsync('estimate-filter');
            const filter = getNormalizedFilter(result);
            return {
                ...defaultEstimateFilter,
                ...filter,
            };
        } catch {
            return defaultEstimateFilter;
        }
    };

    getEnabledEstimateStatuses = (): EstimateStatus[] => {
        if (!this.portalStore.user) {
            // Show all statuses for loginless users
            return Object.keys(EstimatesFilterSettings).map(key => Number(key));
        }

        return getEnabledSettingStatuses(
            EstimatesFilterSettings,
            this.settingsFilter as EstimateSettingsFiltersRecord
        );
    };
}
